import { Button, Center, Text, VStack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { object } from 'yup';

import { Input } from '../../../../../components/formComponents';
import PrimaryButton from '../../../../../components/ui/buttons/PrimaryButton';
import { useExternalCustomerUpdateMutation } from '../../../../../gql/generated/graphql';
import { usePatchLeadByExternalIdData } from '../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdData';
import { logger } from '../../../../../services/sentry';
import { phoneValidationRequired } from '../../../../../utils/validation/phoneNumber';

const editSchema = object({ phone_number: phoneValidationRequired });

type EditPhoneNumberFormProps = {
  externalId: string;
  customerId: string;
  initialPhone: string;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
};

const EditPhoneNumberForm = ({
  externalId,
  customerId,
  initialPhone,
  onSaved,
  onCancel,
}: EditPhoneNumberFormProps) => {
  const { mutateAsync: patchLead } = usePatchLeadByExternalIdData();
  const [customerUpdate] = useExternalCustomerUpdateMutation();
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSubmit = async (values: { phone_number: string }) => {
    setSaveError(null);
    try {
      await patchLead({ externalId, data: { phone: values.phone_number } });
    } catch {
      setSaveError("We couldn't save that number. Please try again.");
      return;
    }

    if (customerId) {
      try {
        await customerUpdate({
          variables: { customerId, customer: { phone_number: values.phone_number } },
        });
      } catch (error) {
        logger.error(
          'EditPhoneNumberForm',
          'externalCustomerUpdate phone sync failed',
          { customerId },
          error,
        );
      }
    }

    await onSaved();
  };

  return (
    <Formik
      initialValues={{ phone_number: initialPhone }}
      validationSchema={editSchema}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <VStack spacing={4}>
            <Text textAlign="center">Update the number we'll text your code to.</Text>
            <Input
              label="Phone Number"
              name="phone_number"
              mask="(999) 999-9999"
              type="tel"
              placeholder="Phone Number"
              autoFocus
            />
            {saveError && (
              <Text fontSize="sm" color="leaseEndRed" textAlign="center">
                {saveError}
              </Text>
            )}
            <Center w="100%">
              <PrimaryButton type="submit" loading={isSubmitting} value="SAVE NUMBER" />
            </Center>
            <Button variant="link" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

export default EditPhoneNumberForm;
